import React, { FC, useRef } from 'react';
import { isWeb } from '@helpers';
import ProgressiveImageWeb from '@v2/components/imageHandling/components/ProgressiveImage/ProgressiveImageWeb';
import ProgressiveImageTabletMobile, {
  ProgressiveImageTabletMobileProps,
} from '@v2/components/imageHandling/components/ProgressiveImage/ProgressiveImageTabletMobile';
import { PictureProps } from '@v2/components/imageHandling/components/Picture';
import { FlatList, View } from 'react-native';
import { useSharedValue, SharedValue } from 'react-native-reanimated';
import { useHandleImageScroll } from './hooks/useHandleImageScroll';
import { fullHeight, fullWidth, scale as initialScale } from '@components/frame';
import { font as initialFont } from '@styles/fonts';
import Preview from '@components/imageHandling/components/thumbnails';
import { usePictureContext } from '@v2/components/imageHandling/providers/PictureProvider/usePictureContext';
import TouchableElement from '@components/common/touchableElement';

interface BigSliderProps
  extends Pick<
    PictureProps,
    | 'images'
    | 'selectedIndex'
    | 'width'
    | 'height'
    | 'setSelectedIndex'
    | 'scale'
    | 'font'
    | 'onImagePress'
    | 'bg'
    | 'singleImageInBigSlider'
    | 'withNotchTop'
    | 'withoutAttachItems'
    | 'zoomableOverlay'
  > {
  scrollProgress?: SharedValue<number>;
}

const BigSlider: FC<BigSliderProps> = ({
  onImagePress,
  images,
  selectedIndex,
  setSelectedIndex,
  scrollProgress,
  singleImageInBigSlider,
  withoutAttachItems,
  withNotchTop,
  zoomableOverlay,
  bg = 'transparent',
  width = fullWidth,
  height = fullHeight,
  scale = initialScale,
  font = initialFont,
}) => {
  const selectedImage = images?.[selectedIndex];
  const flatListRef = useRef<FlatList>(null);
  const internalScrollProgress = useSharedValue(0);
  const currentScrollProgress = scrollProgress || internalScrollProgress;

  const { handleMainSliderImageScrollEnd, handleMainSliderImageScroll } = useHandleImageScroll({
    imageWidth: width,
    activeIndex: selectedIndex,
    setActiveIndex: setSelectedIndex,
    scrollProgress: currentScrollProgress,
    flatListRef,
  });
  const { onImageZoomStart } = usePictureContext();

  if (!selectedImage) return null;

  const props: Omit<ProgressiveImageTabletMobileProps, 'C'> = {
    image: selectedImage,
    withScalingPanning: true,
    containerStyle: { backgroundColor: bg },
    onImagePress,
    withoutAttachItems,
    withNotchTop,
    width,
    height,
    scale,
    font,
    zoomableOverlay,
  };

  if (selectedImage.isDocument) {
    return (
      <TouchableElement onPress={onImagePress}>
        {() => (
          <View style={{ flex: 1, width, height }}>
            <Preview item={selectedImage} scale={scale} font={font} width={width} height={height} bg={bg} processingContainerStyles={{}} />
          </View>
        )}
      </TouchableElement>
    );
  }

  if (isWeb) {
    return <ProgressiveImageWeb {...props} />;
  }

  if (singleImageInBigSlider) {
    return <ProgressiveImageTabletMobile {...props} image={selectedImage} onZoomStart={onImageZoomStart} withScalingPanning />;
  }

  return (
    <FlatList
      ref={flatListRef}
      data={images}
      renderItem={({ item }) => <ProgressiveImageTabletMobile {...props} image={item} onZoomStart={onImageZoomStart} withScalingPanning />}
      keyExtractor={(item) => item.contentHash}
      horizontal
      inverted
      onScroll={handleMainSliderImageScroll}
      onMomentumScrollEnd={handleMainSliderImageScrollEnd}
      decelerationRate={0}
      snapToInterval={width}
      snapToAlignment="center"
      scrollEventThrottle={16}
      showsHorizontalScrollIndicator={false}
      pagingEnabled={false}
      getItemLayout={(_, index) => ({
        length: width,
        offset: width * index,
        index,
      })}
      initialScrollIndex={selectedIndex}
      windowSize={3}
      maxToRenderPerBatch={2}
      removeClippedSubviews={false}
    />
  );
};

export default BigSlider;
